'use client';

import { createRoute, Link } from '@tanstack/react-router';
import { useTheme } from 'next-themes';
import { type ReactNode, Suspense, useEffect, useState } from 'react';

import MarketingLayout from '@/components/layouts/MarketingLayout';
import { WaitlistCTA } from '@/components/marketing/WaitlistCTA';
import { Skeleton } from '@/components/ui';
import { appHasMounted, markAppMounted } from '@/hooks/appMounted';
import {
  CLI_INSTALL_COMMAND_UNIX,
  CLI_INSTALL_COMMAND_WINDOWS,
  CLI_RELEASES_URL,
} from '@/lib/constants';
import { githubAppInstallEnabled } from '@/lib/flags';
import { seoHead } from '@/lib/seo/routeMeta';
import { theme } from '@/lib/ui/theme';

import { Route as rootRoute } from './__root';

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/getting-started',
  head: () => seoHead('/getting-started'),
  component: GettingStarted,
});

function GettingStartedContent() {
  const { resolvedTheme } = useTheme();
  const [mounted, setMounted] = useState(appHasMounted());

  useEffect(() => {
    markAppMounted();
    setMounted(true);
  }, []);

  const showLoading = !mounted;
  const isDark = mounted && resolvedTheme === 'dark';

  const cardStyle = {
    background: theme.box.purple.bg[isDark ? 'dark' : 'light'],
    border: `1px solid ${theme.box.purple.border[isDark ? 'dark' : 'light']}`,
  };
  const codeStyle = {
    background: theme.box.purpleNumber.bg[isDark ? 'dark' : 'light'],
    color: theme.box.purpleNumber.color[isDark ? 'dark' : 'light'],
  };

  const githubSteps: { title: string; body: ReactNode }[] = [
    {
      title: 'Install the GitHub App',
      body: 'Add Mindrealm to your organization and pick the repositories it should review. No CI configuration to write.',
    },
    {
      title: 'Open a pull request',
      body: 'Mindrealm clones the branch, reviews every change, and posts a check run with findings annotated on the exact lines that need them.',
    },
    {
      title: 'Fix findings and push again',
      body: (
        <>
          Using the <code style={codeStyle}>mind review</code> CLI or the Claude
          Code, Codex, Kimi Code, or Antigravity CLI hook, automatically address
          the findings and push again.
        </>
      ),
    },
  ];

  if (showLoading) {
    return (
      <MarketingLayout isLoading={true} isSignedIn={false}>
        <section className="px-6 pt-24 pb-16 md:px-16 md:pt-32">
          <div className="mx-auto max-w-[760px]">
            <Skeleton className="mx-auto mt-2 mb-6 h-12 w-[55%]" />
            <Skeleton className="mx-auto mb-2 h-6 w-[85%]" />
            <Skeleton className="mx-auto mb-12 h-6 w-[55%]" />
            {/* "Review pull requests" heading + 3 numbered steps */}
            <Skeleton className="mb-6 h-8 w-[45%]" />
            <div className="mb-12 space-y-4">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-32 w-full rounded-xl" />
              ))}
            </div>
            {/* Install button + trial note */}
            <div className="mb-12 flex flex-col items-center">
              <Skeleton className="mb-3 h-12 w-52 rounded-md" />
              <Skeleton className="h-4 w-40" />
            </div>
            {/* "Run it locally" heading + the tall CLI card */}
            <Skeleton className="mb-6 h-8 w-[35%]" />
            <div className="border-border space-y-6 rounded-xl border p-6 md:p-8">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="space-y-2">
                  <Skeleton className="h-5 w-[70%]" />
                  <Skeleton className="h-10 w-full rounded-md" />
                </div>
              ))}
            </div>
          </div>
        </section>
      </MarketingLayout>
    );
  }

  return (
    <MarketingLayout isLoading={false} isSignedIn={false}>
      <section className="px-6 pt-24 pb-16 md:px-16 md:pt-32">
        <div className="mx-auto max-w-[760px]">
          <h1
            className="text-gradient-hero mb-4 text-center text-4xl font-bold tracking-tight md:text-5xl"
            style={{ fontFamily: theme.fonts.display, lineHeight: 1.2 }}
          >
            Get started in minutes.
          </h1>
          <p
            className="text-foreground/70 mb-12 text-center text-lg md:text-xl"
            style={{ fontFamily: theme.fonts.body }}
          >
            Review pull requests through the GitHub App, run Mindrealm locally
            from your terminal, and your agent's stop hook.
          </p>

          {/* GitHub App path */}
          <h2
            className="mb-6 text-2xl font-bold md:text-3xl"
            style={{ fontFamily: theme.fonts.display }}
          >
            Review pull requests
          </h2>
          <div
            className="mb-12 space-y-4"
            style={{ fontFamily: theme.fonts.body }}
          >
            {githubSteps.map((step, index) => (
              <div
                key={step.title}
                className="flex gap-4 rounded-xl p-6"
                style={cardStyle}
              >
                <span
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-lg font-bold"
                  style={{
                    background:
                      theme.box.accentNumber.bg[isDark ? 'dark' : 'light'],
                    color:
                      theme.box.accentNumber.color[isDark ? 'dark' : 'light'],
                  }}
                >
                  {index + 1}
                </span>
                <div>
                  <h3
                    className="mb-1 text-lg font-semibold"
                    style={{ fontFamily: theme.fonts.display }}
                  >
                    {step.title}
                  </h3>
                  <p className="text-foreground/70 text-base">{step.body}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="mb-12 text-center">
            <WaitlistCTA
              label="Install the GitHub App"
              source="getting_started"
            />
            <p className="text-foreground/60 mt-3 text-sm">
              {githubAppInstallEnabled()
                ? 'Starts your 7-day free trial.'
                : 'Approved teams onboard directly with the founder.'}
            </p>
          </div>

          {/* CLI path */}
          <h2
            id="download"
            className="mb-6 scroll-mt-24 text-2xl font-bold md:text-3xl"
            style={{ fontFamily: theme.fonts.display }}
          >
            Run it locally
          </h2>
          <div
            className="space-y-6 rounded-xl p-6 md:p-8"
            style={{ ...cardStyle, fontFamily: theme.fonts.body }}
          >
            <div>
              <p className="text-foreground/80 mb-2">
                Install the <code style={codeStyle}>mind</code> CLI on macOS or
                Linux:
              </p>
              <pre
                className="overflow-x-auto rounded-md p-3 text-sm"
                style={codeStyle}
              >
                <code>{CLI_INSTALL_COMMAND_UNIX}</code>
              </pre>
              <p className="text-foreground/80 mt-4 mb-2">
                On Windows, in PowerShell:
              </p>
              <pre
                className="overflow-x-auto rounded-md p-3 text-sm"
                style={codeStyle}
              >
                <code>{CLI_INSTALL_COMMAND_WINDOWS}</code>
              </pre>
              <p className="text-foreground/60 mt-2 text-sm">
                Or download a binary for your platform from the{' '}
                <a
                  href={CLI_RELEASES_URL}
                  className="underline"
                  style={{ textDecorationColor: theme.colors.semantic.info }}
                >
                  releases page
                </a>
                , then extract it and put <code style={codeStyle}>mind</code> on
                your PATH.
              </p>
              <p className="text-foreground/60 mt-2 text-sm">
                Re-run the same command any time to update.
              </p>
            </div>
            <div>
              <p className="text-foreground/80 mb-2">Then authenticate:</p>
              <pre
                className="overflow-x-auto rounded-md p-3 text-sm"
                style={codeStyle}
              >
                <code>mind login</code>
              </pre>
            </div>
            <div>
              <p className="text-foreground/80 mb-2">
                From inside a repository connected to your GitHub App, review
                your changes:
              </p>
              <pre
                className="overflow-x-auto rounded-md p-3 text-sm"
                style={codeStyle}
              >
                <code>mind review</code>
              </pre>
            </div>
            <div>
              <p className="text-foreground/80 mb-2">
                Wire it into Claude Code, Codex, Kimi Code, or Antigravity CLI
                so it reviews automatically whenever your agent finishes. One
                command detects your agent and sets up the auto-fix loop:
              </p>
              <pre
                className="overflow-x-auto rounded-md p-3 text-sm"
                style={codeStyle}
              >
                <code>mind setup</code>
              </pre>
            </div>
            <div>
              <p className="text-foreground/80 mb-2">
                Disagree with a finding? Silence one check with a{' '}
                <code style={codeStyle}>mind:disable</code> comment on the line
                above it. It stays recorded and reviewable, never hidden:
              </p>
              <pre
                className="overflow-x-auto rounded-md p-3 text-sm"
                style={codeStyle}
              >
                <code>{`// mind:disable                (Go, TypeScript, Rust)
# mind:disable                 (Python)
// mind:disable unused-export  (silence just one rule)`}</code>
              </pre>
            </div>
            <div>
              <p className="text-foreground/80">
                See the{' '}
                <Link
                  to="/docs"
                  className="underline"
                  style={{ textDecorationColor: theme.colors.semantic.info }}
                >
                  docs
                </Link>{' '}
                for the full configuration reference, every command, and
                suppressions.
              </p>
            </div>
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}

function GettingStarted() {
  return (
    <Suspense
      fallback={
        <MarketingLayout isLoading={true} isSignedIn={false}>
          <section className="px-6 pt-24 pb-16 md:px-16 md:pt-32">
            <div className="mx-auto max-w-[760px]">
              <Skeleton className="mx-auto mt-2 mb-6 h-12 w-[55%]" />
              <Skeleton className="mx-auto h-24 w-full rounded-xl" />
            </div>
          </section>
        </MarketingLayout>
      }
    >
      <GettingStartedContent />
    </Suspense>
  );
}
